import { Button, FormControl, Grid, InputLabel, MenuItem } from '@material-ui/core';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Field, FieldArray, getFormValues } from 'redux-form/immutable';

import FormDateSelector from 'retired/FormDateSelector';
import FormFileUpload from 'retired/FormFileUpload';
import FormNonprofitsSelector from 'retired/FormNonprofitsSelector';
import FormSelect from 'retired/FormSelect';
import FormTextField from 'retired/FormTextField';
import { selectUser } from 'entities/user/selectors';
import { currencies } from 'utils/currencyFormat';
import { maxLength } from 'utils/validators';

const CurrencyCodeSelector = (props): ReactElement => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <FormSelect displayEmpty {...props}>
    {[{ currencyCode: '', currency: { label: '' } }]
      .concat(
        Object.keys(currencies).map((currencyCode) => ({
          currencyCode,
          currency: currencies[currencyCode],
        }))
      )
      .map(({ currencyCode, currency }) => (
        <MenuItem key={currencyCode} value={currencyCode}>
          {currency.label}
        </MenuItem>
      ))}
  </FormSelect>
);

const validateAmount = (value) => {
  if (value !== '') {
    if (Number(value) <= 0) {
      return 'Donation amount must be positive';
    }

    return undefined;
  }

  return 'Donation amount is required';
};

const validateCurrency = (value) => {
  if (!value) {
    return 'Donation currency is required';
  }

  return undefined;
};

const validateDate = (value) => {
  if (!value) {
    return 'Donation date is required';
  }

  if (value.valueOf() > Date.now()) {
    return 'Donation date must be in the past';
  }

  return undefined;
};

const validateNonprofits = (value) => {
  if (!value || value.length < 1) {
    return 'You must select a nonprofit';
  }

  if (value.length > 1) {
    return 'You must select a single nonprofit';
  }

  return undefined;
};

export const CorporateDonationLogForm = ({ invalid, pristine, submitting, form }) => {
  const user = useSelector(selectUser);
  const formValues = useSelector(getFormValues(form));
  const currencySymbol = currencies[formValues?.get('currency')]?.symbol;

  return (
    <div>
      <div
        style={{
          marginTop: 30,
        }}
      >
        <FieldArray
          name="nonprofits"
          component={FormNonprofitsSelector}
          limit={1}
          initialLocation={user?.organization?.locationObject?.countryCode}
          nonprofitsAreEditable={false}
          required
          validate={validateNonprofits}
        />
      </div>
      <div
        style={{
          marginTop: 30,
        }}
      >
        <Grid container spacing={24}>
          <Grid item md={4}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                height: '100%',
              }}
            >
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>Donation date*</InputLabel>
              </FormControl>
              <Field
                name="date"
                component={FormDateSelector}
                minDate={moment().endOf('day')}
                isNextDateDisabled
                required
                noTime
                validate={validateDate}
              />
            </div>
          </Grid>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Donation Currency*</InputLabel>
            </FormControl>
            <Field
              name="currency"
              component={CurrencyCodeSelector}
              required
              style={{
                minWidth: 200,
              }}
              validate={validateCurrency}
            />
          </Grid>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Donation Amount*</InputLabel>
            </FormControl>
            {currencySymbol}
            &nbsp;
            <Field
              name="amount"
              component={FormTextField}
              type="number"
              required
              style={{
                minWidth: 200,
              }}
              validate={validateAmount}
            />
          </Grid>
        </Grid>
      </div>
      <div style={{ marginTop: 30 }}>
        <FormControl style={{ width: '100%', height: 17 }}>
          <InputLabel shrink>Donation notes</InputLabel>
        </FormControl>
        <Field
          name="note"
          component={FormTextField}
          helperText="Add any other relevant donation details. Up to 300 characters"
          multiline
          rows={3}
          inputProps={{ maxLength: 300 }}
          validate={maxLength(300)}
          style={{ width: '100%' }}
        />
      </div>
      <div style={{ marginTop: 30 }}>
        <Field
          name="receiptUrl"
          component={FormFileUpload}
          labelText="DONATION DOCUMENTATION"
          uploadCta="Upload a receipt or other document"
          linkPreview
        />
      </div>
      <div style={{ marginTop: 30, textAlign: 'right' }}>
        <Button
          type="submit"
          disabled={pristine || invalid || submitting}
          color="primary"
          variant="contained"
        >
          Complete
        </Button>
      </div>
    </div>
  );
};

export default CorporateDonationLogForm;
